import { HStack, useDisclosure, Wrap, WrapItem } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import difference from 'lodash-es/difference';
import sortBy from 'lodash-es/sortBy';
import uniqBy from 'lodash-es/uniqBy';
import useLinkPatients from 'pages/Conversations/hooks/useLinkPatients';
import { toggleConversationDetailsModal } from 'pages/Conversations/state/conversationsSlice';
import { getChannelParticipants } from 'pages/Conversations/utils';
import React, { useCallback, useEffect, useMemo } from 'react';
import PetAvatar from 'shared/components/Avatars/PetAvatar';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { useSidePanel } from 'shared/components/SidePanel/state/providers/SidePanelProvider';
import { GraphQLFetchPolicies } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import useLocationState from 'shared/hooks/useLocationState';
import {
  ChannelViewChannelMemberFragment,
  ChannelViewChannelPetFragment,
  useGetChannelPetsLazyQuery,
} from 'shared/types/graphql';
import { useAppDispatch } from 'state/hooks';
import { PatientItem } from './PatientItem';

interface IPatientsToolbarProps {
  linkedChannelPets?: ChannelViewChannelPetFragment[];
  channelId?: string;
  channelMembers?: ChannelViewChannelMemberFragment[];
  refetchChannel: () => void;
}

const PatientsToolbar = ({
  linkedChannelPets = [],
  channelId,
  channelMembers,
  refetchChannel,
}: IPatientsToolbarProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const { addPatients } = useLinkPatients(channelId);
  const { channelPetParentIds } = getChannelParticipants(channelMembers);
  const { isOpen, onToggle } = useDisclosure();
  const dispatch = useAppDispatch();
  const { viewClinicPet } = useSidePanelSearch();
  const { setIsOpen } = useSidePanel();
  const [{ linkPatientsToConversation }, setLocationState] = useLocationState();

  const [getDropdownPetOptions, { data: pets }] = useGetChannelPetsLazyQuery({
    variables: {
      where: {
        id: {
          in: channelPetParentIds,
        },
      },
      petsWhere: {
        clinicId: { equals: currentClinicId || '' },
        isDeleted: { equals: false },
      },
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const allChannelPets = useMemo(() => {
    if (!pets?.findManyClinicPetParent) return [];
    return uniqBy(pets.findManyClinicPetParent.map((petParents) => petParents.pets).flat(), 'id');
  }, [pets]);

  const patientOptions = useMemo(() => {
    if (!allChannelPets) return [];

    const petList = allChannelPets.map((pet) => ({
      label: pet.name,
      leftElement: <PetAvatar speciesType={pet?.speciesType} isDeceased={pet?.computedIsDeceased} iconOnly />,
      value: pet.id,
      isSelected: !!linkedChannelPets?.find((p) => p.id === pet.id),
      groupLabel: pet.computedIsActive ? 'Active' : 'Inactive',
    }));

    return sortBy(petList, ['groupLabel']);
  }, [allChannelPets, linkedChannelPets]);

  const linkedPets = useMemo(
    () => (isOpen ? linkedChannelPets : [...linkedChannelPets].slice(0, 2)),
    [isOpen, linkedChannelPets],
  );

  const remainingPets = useMemo(
    () => linkedChannelPets?.length - linkedPets?.length,
    [linkedChannelPets?.length, linkedPets?.length],
  );

  const shouldShowMore = useMemo(() => linkedChannelPets?.length > 2, [linkedChannelPets?.length]);

  const goToPet = useCallback(
    async (clinicPetId: string): Promise<void> => {
      viewClinicPet({ clinicPetId });
    },
    [viewClinicPet],
  );

  const handlePetClick = (id: string): void => {
    if (id) {
      setIsOpen(true);
      goToPet(id);
    }
  };

  const closeDetailsModal = useCallback(() => {
    dispatch(toggleConversationDetailsModal({ isOpen: true }));
  }, [dispatch]);

  useEffect(() => {
    if (linkPatientsToConversation && linkPatientsToConversation.length > 0) {
      getDropdownPetOptions();
    }
  }, [getDropdownPetOptions, linkPatientsToConversation]);

  useEffect(() => {
    if (!linkPatientsToConversation?.length || !patientOptions?.length) return;
    const patientIdsToLink = difference(
      linkPatientsToConversation,
      patientOptions.filter((o) => o.isSelected).map((o) => o.value),
    );
    setLocationState({ linkPatientsToConversation: null });
    if (patientIdsToLink.length) {
      addPatients(patientIdsToLink);
    }
  }, [linkPatientsToConversation, patientOptions, addPatients, setLocationState]);

  return (
    <HStack
      className="ChannelView__PatientsToolbar"
      px={4}
      py={2}
      bg="background.subtle"
      borderBottom="1px"
      borderBottomColor="border.default"
    >
      {!!linkedPets?.length && (
        <Wrap spacing={1}>
          {linkedPets.map((pet) => (
            <WrapItem key={pet.id} alignItems="center">
              <PatientItem pet={pet} handlePetClick={handlePetClick} refetchChannel={refetchChannel} />
            </WrapItem>
          ))}
          {shouldShowMore && (
            <WrapItem alignItems="center">
              <Button size="sm" variant="ghostNeutral" onClick={onToggle}>{`${
                isOpen ? 'Show Less' : `+ ${remainingPets} More `
              }`}</Button>
            </WrapItem>
          )}
        </Wrap>
      )}
      <Button size="sm" variant="secondary" onClick={closeDetailsModal}>
        Link Patient
      </Button>
    </HStack>
  );
};

export default PatientsToolbar;
